import { forwardRef } from 'react';
import { PageRender } from '../page-render';
import { mapValueToBlock } from '../../helper-to-map';
import { TemplateEditorConfigProps } from '@/utils/transaction/template-editor/type';
import { BlockProps } from '@/utils/transaction/template-editor/component/content-blocks/type';
import { AccountBillContentBlock } from '@/utils/transaction/template-editor/component/content-blocks/account';
import { decryptData } from '@/components/reusable/crypto/Cipher';
import {
  getBsFromAD,
  // getAdFromBSInYMD,
  getTodayDate,
} from '@/common/custom-date-selector-list/helpers';
import useAppData from '@/hooks/useAppData';
import { toDecimal } from '@/pages/transaction/purchase/helpers';
import { toWords, toWordsUSD } from '@/utils/transactionbill/toWordsConfig';
// import {
//   footerImageBase64,
//   headerImageBase64,
// } from '../../default-templates/images';
// import { ShowAllData } from '@/apiError/apiShow';
// import { getTodaysDate, getTodaysDateMYD } from '@/utils/date/TodaysDate';
// import NepaliDateConverter from 'nepali-date-converter';

interface BillPrintMapperProps {
  editorConfig?: TemplateEditorConfigProps;
  data?: { [key: string]: any };
  blocks?: BlockProps[];
  copies: Array<string>;
  looplength: number;
  print_type?: string;
  voucherType?: boolean;
  isHidden?: boolean;
  isPrint?: boolean;
}

export const BillPrintMapper = forwardRef<HTMLDivElement, BillPrintMapperProps>(
  (props, ref) => {
    const {
      data,
      editorConfig,
      blocks,
      voucherType = false,
      isHidden = true,
      isPrint = false,
    } = props;

    const { app } = useAppData();

    const detailsData = data?.detailsData?.map((item: any) => {
      // const diamondData = item?.diamondData || [];
      const stoneAmount =
        item?.stoneAmount ||
        item?.stoneData.reduce((total: any, item: any) => {
          if (item?.itemTypeName?.toLowerCase() === 'stone') {
            return total + item?.amount || 0;
          }
          return total;
        }, 0);

      return {
        ...item,
        stoneAmount,
      };
    });

    if (!editorConfig || !blocks)
      return (
        <div className="hidden">
          <div ref={ref}>No Template to Print</div>
        </div>
      );

    const raw = localStorage.getItem('userData');
    const userData: any = raw ? decryptData(raw) : null;

    const goldRateInTola = () => {
      if (!data?.detailsData?.[0]?.rate) return 0;
      if (app?.primaryCalculationType === 'gram')
        return (data?.detailsData?.[0]?.rate * 11.664).toFixed(3);
      if (app?.primaryCalculationType === 'lal')
        return (data?.detailsData?.[0]?.rate * 100).toFixed(3);
      return 0;
    };

    const voucherData: any =
      data?.summaryData?.voucherData?.voucherEntries || [];
    const paymentModes: string = voucherData
      .filter(
        (entry: any) =>
          entry?.groupName?.toLowerCase() === 'cash' ||
          entry.groupName?.toLowerCase() === 'bank',
      )
      .map((entry: any) => entry.groupName)
      .join('/ ');
    const imageList =
      data?.detailsData?.flatMap((item: Record<string, any>) =>
        Array.isArray(item?.images)
          ? item.images.map((img: Record<string, string>) => img?.image)
          : [],
      ) || [];

    const headerMapping = {
      ...data?.customerDetails,
      ...userData,
      ...data?.summaryData,
      paymentMode: paymentModes,
      voucherNo: data?.voucherNo,
      voucherDate: data?.voucherDate,
      voucherDateBs: getBsFromAD(data?.voucherDate),
      printCountMessage: data?.summaryData?.printCountMessage,
      voucherType: data?.voucherType,
      narration: data?.narration,
      todayGoldRate: goldRateInTola(),
      images: imageList,
      ...(data?.summaryData?.invoiceMiti
        ? {
            date:
              data?.summaryData?.salesDate || data?.summaryData?.purchaseDate,
          }
        : {}),
    };

    const now = new Date();
    const printedDateTimeAd = `${now?.getFullYear()}/${now?.getMonth() + 1}/${now?.getDate()} ${now?.getHours()}:${now?.getMinutes()}:${now?.getSeconds()}`;

    const time = `${now?.getHours()}:${now?.getMinutes()}:${now?.getSeconds()}`;

    const goldRateInTolaFooter = (rate: number) => {
      if (!rate) return 0;

      if (app?.primaryCalculationType === 'gm') {
        return (rate * 11.664).toFixed(3);
      }
      if (app?.primaryCalculationType === 'lal') {
        return (rate * 100).toFixed(3);
      }
      return 0;
    };

    const commonData = {
      purchaseAmount: Number(data?.summaryData?.purchaseAmount || 0),
      receiptAmount:
        data?.summaryData?.voucherData?.voucherEntries?.reduce(
          (sum: any, item: any) => sum + Number(item?.debitAmount || 0),
          0,
        ) ?? 0,
      adjustmentAmount: Number(data?.summaryData?.adjustmentAmount || 0),
      vatAmount:
        data?.detailsData?.reduce(
          (sum: any, item: any) => sum + Number(item?.vatAmount || 0),
          0,
        ) ?? 0,
      grossTotal:
        data?.detailsData?.reduce(
          (sum: any, item: any) =>
            sum + Number(item?.subTotal || item?.netAmount || 0),
          0,
        ) ?? 0,

      taxableAmount:
        data?.summaryData?.taxableAmount ||
        data?.detailsData?.reduce(
          (sum: number, item: any) => sum + Number(item?.taxableAmount || 0),
          0,
        ) ||
        (data?.detailsData?.reduce(
          (sum: number, item: any) =>
            sum + Number(item?.subTotal || item?.netAmount || 0),
          0,
        ) || 0) -
          (data?.detailsData?.reduce(
            (sum: number, item: any) => sum + Number(item?.discountAmount || 0),
            0,
          ) || 0) -
          Number(data?.summaryData?.purchaseAmount || 0),
      grandTotal:
        data?.summaryData?.netAmount ||
        data?.detailsData?.reduce(
          (sum: any, item: any) => sum + Number(item?.netAmount || 0),
          0,
        ) - (data?.summaryData?.purchaseAmount || 0) ||
        0,

      taxAmount:
        data?.detailsData?.reduce(
          (sum: any, item: any) => sum + Number(item?.luxuryTaxAmount || 0),
          0,
        ) ?? 0,

      discountAmount: Number(data?.summaryData?.discountAmount || 0),
      goldAmount: Number(data?.detailsData?.[0]?.rate || 0),
      balanceAmount:
        Number(
          data?.summaryData?.balanceAmount ||
            data?.summaryData?.netAmount -
              (data?.summaryData?.purchaseAmount || 0) -
              data?.summaryData?.voucherData?.voucherEntries?.reduce(
                (sum: any, item: any) =>
                  sum +
                  toDecimal(item?.creditAmount || 0) -
                  data?.summaryData?.adjustmentAmount,
                0,
              ),
        ) || 0,

      goldRate: `${app?.currencySymbol}
                            ${goldRateInTolaFooter(data?.detailsData?.[0]?.todayRate as number) || 0}`,
      metalWeight:
        app?.primaryCalculationType?.toLowerCase() === 'lal'
          ? '(100 Lal)'
          : '( 11.664GMS )',
    };

    // Map receipt voucher entries to the footer mapping
    const receiptVoucherMapping: Record<string, any> = {};
    (data?.summaryData?.voucherData?.voucherEntries || [])
      .filter((e: any) => parseFloat(e.debitAmount) > 0)
      .forEach((e: any, i: number) => {
        const n = i + 1;
        receiptVoucherMapping[`receipt-voucher-${n}-name`] = e.ledgerName;
        receiptVoucherMapping[`receipt-voucher-${n}-amount`] = e.debitAmount;
        receiptVoucherMapping[`receipt-voucher-${n}-group`] = e.groupName;
      });

    const contentBlock: any = blocks
      ?.filter((item) => item?.isVisible)
      .find((item) => item?.location === 'content');
    const headerBlocks = blocks
      .filter((block) => block.isVisible && block.location === 'header')
      .map((block) => mapValueToBlock(block, { ...headerMapping }));

    const column_name = contentBlock?.data?.column_name;

    const table_footer_names = contentBlock?.data?.table_footer_names;

    // footer data mapping
    const footerMapping = {
      ...data?.footer_data,
      ...data?.summaryData,
      ...userData,
      ...receiptVoucherMapping,
      ...commonData,
      images: imageList,
      agentName: data?.summaryData?.agentName,
      printedDateAd: printedDateTimeAd,
      printedDate: getTodayDate() + ' ' + time,
      inWords: `${contentBlock?.currencyType} ${
        contentBlock?.currencyType === '$'
          ? toWordsUSD.convert(
              !data?.taxableAmount
                ? Number(commonData?.grossTotal ?? 0) -
                    Number(commonData?.discountAmount ?? 0) +
                    Number(commonData?.taxAmount ?? 0)
                : Number(commonData?.taxableAmount ?? 0) +
                    Number(commonData?.vatAmount ?? 0) +
                    Number(commonData?.taxAmount ?? 0),
            )
          : toWords.convert(
              !data?.taxableAmount
                ? Number(commonData?.grossTotal ?? 0) -
                    Number(commonData?.discountAmount ?? 0) +
                    +Number(commonData?.taxAmount ?? 0)
                : Number(commonData?.taxableAmount ?? 0) +
                    Number(commonData?.vatAmount ?? 0) +
                    Number(commonData?.taxAmount ?? 0),
            )
      }`,
    };

    return (
      <div className={`${isHidden ? 'hidden' : ''}`}>
        <div ref={ref}>
          {/* {copies?.map((copy_type, index) => ( */}
          {/* {isPrint && data?.summaryData?.printCountMessage} */}
          <PageRender
            config={editorConfig}
            headerBlocks={headerBlocks}
            watermark={{
              width: editorConfig?.printPreference?.watermark?.width || 0,
              height: editorConfig?.printPreference?.watermark?.height || 0,
              name: editorConfig?.printPreference?.watermark?.name || null,
            }}
            // headerImage={{
            //   name: headerImageBase64,
            //   height: 857,
            //   width: 5174,
            // }}
            // footerBlocks={
            //   (copy_type !== "OFFICE COPY" ? index === loopLength - 1 : index - loopLength === loopLength - 1)
            //     ? blocks
            //         .filter((item) => item.isVisible && item.location === "footer")
            //         .map((block) => mapValueToBlock(block, { ...mappingSource, copy_type }))
            //         .filter((item) => {
            //           if (print_type === "Original") {
            //             return item?.key !== "109"; //key exist in createBlock
            //           }
            //           return true; // Reprint Case
            //         })
            //     : []
            // }

            footerBlocks={blocks
              .filter((block) => block.isVisible && block.location === 'footer')
              .map((block) => mapValueToBlock(block, { ...footerMapping }))}
            contentX={contentBlock?.x ?? 0}
            contentY={contentBlock?.y ?? 0}
            contentBlock={
              contentBlock ? (
                <AccountBillContentBlock
                  config={editorConfig}
                  blockProps={{
                    ...contentBlock,
                    type: contentBlock?.type,
                    url: userData?.companyImage,
                    data:
                      contentBlock?.type === 'receipt' ||
                      contentBlock?.type === 'payment' ||
                      contentBlock?.type === 'journal' ||
                      contentBlock?.type === 'contra'
                        ? {
                            ...data,
                          }
                        : {
                            ...contentBlock?.data,
                            ...data?.summaryData,
                            column_name,
                            table_footer_names,
                            items:
                              detailsData?.map((item: any) => {
                                const netAmount = Number(item?.netAmount || 0);
                                // Number(item?.taxableAmount || 0) +
                                // Number(item?.luxuryTaxAmount || 0) +
                                // Number(item?.vatAmount || 0);

                                const floatedNetAmt = parseFloat(
                                  netAmount.toFixed(3),
                                );

                                return {
                                  ...item,
                                  netAmount: Number(floatedNetAmt),
                                };
                              }) || [],
                            purchaseAmount: Number(
                              data?.summaryData?.purchaseAmount || 0,
                            ),
                            receiptAmount:
                              data?.summaryData?.voucherData?.voucherEntries?.reduce(
                                (sum: any, item: any) =>
                                  sum + Number(item?.debitAmount || 0),
                                0,
                              ) ?? 0,
                            adjustmentAmount: Number(
                              data?.summaryData?.adjustmentAmount || 0,
                            ),
                            vatAmount:
                              data?.detailsData?.reduce(
                                (sum: any, item: any) =>
                                  sum + Number(item?.vatAmount || 0),
                                0,
                              ) ?? 0,
                            grossTotal:
                              data?.detailsData?.reduce(
                                (sum: any, item: any) =>
                                  sum +
                                  Number(
                                    item?.subTotal || item?.netAmount || 0,
                                  ),
                                0,
                              ) ?? 0,

                            // taxableAmount:
                            //   data?.detailsData?.reduce(
                            //     (sum: any, item: any) =>
                            //       sum + Number(item?.taxableAmount || 0),
                            //     0,
                            //   ) ?? 0,

                            taxableAmount:
                              data?.detailsData?.reduce(
                                (sum: number, item: any) =>
                                  sum + Number(item?.taxableAmount || 0),
                                0,
                              ) ||
                              (data?.detailsData?.reduce(
                                (sum: number, item: any) =>
                                  sum +
                                  Number(
                                    item?.subTotal || item?.netAmount || 0,
                                  ),
                                0,
                              ) || 0) -
                                (data?.detailsData?.reduce(
                                  (sum: number, item: any) =>
                                    sum + Number(item?.discountAmount || 0),
                                  0,
                                ) || 0) -
                                Number(data?.summaryData?.purchaseAmount || 0),
                            grandTotal: Number(
                              data?.summaryData?.netAmount || 0,
                            ),

                            taxAmount:
                              data?.detailsData?.reduce(
                                (sum: any, item: any) =>
                                  sum + Number(item?.luxuryTaxAmount || 0),
                                0,
                              ) ?? 0,

                            discountAmount: Number(
                              data?.summaryData?.discountAmount || 0,
                            ),
                            goldAmount: Number(
                              data?.detailsData?.[0]?.rate || 0,
                            ),
                            balanceAmount:
                              Number(
                                data?.summaryData?.balanceAmount ??
                                  data?.summaryData?.netAmount -
                                    (data?.summaryData?.purchaseAmount ?? 0) -
                                    (data?.summaryData?.voucherData?.voucherEntries?.reduce(
                                      (sum: any, item: any) =>
                                        sum +
                                        toDecimal(item?.creditAmount ?? 0),
                                      0,
                                    ) ?? 0) -
                                    (data?.summaryData?.adjustmentAmount ?? 0),
                              ) ?? 0,
                          },
                  }}
                  maxWidth={
                    editorConfig?.width -
                    editorConfig?.margin?.left -
                    editorConfig?.margin?.right
                  }
                />
              ) : (
                <div>Template not found</div>
              )
            }
            // contentBlock={contentBlock && isABillContentBlock(contentBlock) ? "" : ""}
            // contentBlock={
            //   contentBlock && isBillContentBlock(contentBlock) ? (
            //     <BillContentBlock
            //       priceShow={copy_type === "OFFICE COPY" ? index - loopLength : index}
            //       loopLength={loopLength}
            //       blockProps={{
            //         ...contentBlock,

            //         data: data
            //           ? {
            //               items: data.bill_details
            //                 .slice(
            //                   copy_type === ""
            //                     ? index * breakLength
            //                     : copy_type === "PATIENT COPY"
            //                     ? index * breakLength
            //                     : copy_type === "OFFICE COPY"
            //                     ? (index - loopLength) * breakLength
            //                     : 0,
            //                   copy_type === ""
            //                     ? endIndex + index * breakLength
            //                     : copy_type === "PATIENT COPY"
            //                     ? endIndex + index * breakLength
            //                     : copy_type === "OFFICE COPY"
            //                     ? endIndex + (index - loopLength) * breakLength
            //                     : data.bill_details.length,
            //                 )
            //                 .map((item: any, indexx: number) => ({
            //                   index:
            //                     copy_type === "OFFICE COPY"
            //                       ? indexx + (index - loopLength) * breakLength
            //                       : indexx + index * breakLength,
            //                   service: item.service,
            //                   item_code: item.test_name_code,
            //                   rate: item.rate,
            //                   quantity: item.qty,
            //                   amount: item.qty * item.rate,
            //                 })),

            //               remarks: data.remarks_v,
            //               payment_method: data?.payment_details || [],
            //               sub_total: data.bill_details.reduce(
            //                 (acc: number, item: any) => acc + item.qty * Number(item.rate),
            //                 0,
            //               ),
            //               discount: data.bill_details.reduce(
            //                 (acc: number, item: any) => acc + Number(item.discount),
            //                 0,
            //               ),
            //               tender_amount: data.tender_amount_v,
            //               due_amount:
            //                 data.pay_type_v === "CREDIT"
            //                   ? data.bill_details.reduce(
            //                       (acc: number, item: any) => acc + Number(item.total_amount),
            //                       0,
            //                     ) - data.tender_amount_v
            //                   : null,
            //               return_amount:
            //                 data.pay_type_v === "CREDIT"
            //                   ? null
            //                   : Math.max(
            //                       data.tender_amount_v -
            //                         data.bill_details.reduce(
            //                           (acc: number, item: any) => acc + Number(item.total_amount),
            //                           0,
            //                         ),
            //                       0,
            //                     ),
            //               total: data.bill_details.reduce(
            //                 (acc: number, item: any) => acc + Number(item.total_amount),
            //                 0,
            //               ),
            //             }
            //           : { ...contentBlock.data, payment_method: [] },
            //       }}
            //       maxWidth={editorConfig.width - editorConfig.margin.left - editorConfig.margin.right}
            //     />
            //   ) : (
            //<div>Template not found</div>
            //   )
            // }
            totalPages={10}
          />
          {/* ))} */}
        </div>
      </div>
    );
  },
);
